/**
 * Горячие клавиши desktop (CLIENT_WEB.md §4.6, экран D-02).
 *
 * Правила раздела, которые здесь соблюдаются буквально:
 *  п. 1 — каждое действие доступно и мышью; клавиша только ускоряет;
 *  п. 3 — перехват системных и браузерных сочетаний ЗАПРЕЩЁН; единственное
 *         исключение по таблице — `Ctrl/Cmd + F` при открытом чате, и оно
 *         живёт в самом чате (C-08), а не здесь;
 *  п. 4 — на mobile и в сенсорных раскладках клавиши не объявляются;
 *  п. 5 — подсказка показывает только фактически перехватываемые сочетания.
 *
 * `Enter` / `Shift+Enter` и `Esc` живут там, где они действуют (композер C-07,
 * оверлеи): контекст решает поведение, а глобальный обработчик о нём не знает.
 */
import { MEDIA } from '@shared/lib/use-media-query'

/** Контейнер списка, по которому ходят `↑`/`↓`. */
export const KEY_LIST_ATTR = 'data-keylist'
/** Элемент такого списка; должен быть фокусируемым сам по себе. */
export const KEY_ITEM_ATTR = 'data-keyitem'
/** Поле поиска по списку диалогов (C-02) — цель `/` и `Ctrl/Cmd + K`. */
export const CHAT_SEARCH_ATTR = 'data-hotkey-chat-search'

export interface HotkeyActions {
  /** `?` — подсказка со списком сочетаний. */
  openShortcuts: () => void
  /** `/` и `Ctrl/Cmd + K` — поиск по списку диалогов. */
  focusChatSearch: () => void
}

/** Перечень для подсказки D-02 — единственный источник, п. 2. */
export const SHORTCUTS: readonly { keys: string; action: string; context: string }[] = [
  { keys: 'Enter', action: 'Отправить сообщение', context: 'фокус в поле ввода чата' },
  { keys: 'Shift + Enter', action: 'Новая строка', context: 'фокус в поле ввода чата' },
  {
    keys: 'Esc',
    action: 'Закрыть панель, окно или выйти из режима ответа и правки',
    context: 'по одному уровню за нажатие',
  },
  { keys: '↑ / ↓', action: 'Перемещение по списку', context: 'фокус в списке; Enter открывает' },
  { keys: '↑', action: 'Правка последнего своего сообщения', context: 'поле ввода чата пустое' },
  { keys: '/', action: 'Поиск по списку диалогов', context: 'фокус не в поле ввода' },
  { keys: 'Ctrl / ⌘ + F', action: 'Поиск по открытому диалогу', context: 'открыт чат' },
  { keys: 'Ctrl / ⌘ + K', action: 'Быстрый переход к диалогу', context: 'глобально' },
  { keys: '?', action: 'Эта подсказка', context: 'фокус не в поле ввода' },
]

/** §4.6 п. 4: на сенсорных раскладках сочетания не объявляются. */
export function hotkeysAvailable(): boolean {
  if (typeof matchMedia !== 'function') return false
  return matchMedia(MEDIA.pointerFine).matches && matchMedia(MEDIA.fromDesktop).matches
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true

  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

/**
 * `↑`/`↓` внутри списка: фокус переезжает на соседний элемент, открытие
 * остаётся за `Enter` — то есть за штатным поведением ссылки или кнопки.
 */
function moveWithinList(direction: 1 | -1): boolean {
  const active = document.activeElement
  const list =
    (active instanceof HTMLElement ? active.closest(`[${KEY_LIST_ATTR}]`) : null) ??
    document.querySelector(`[${KEY_LIST_ATTR}]`)

  if (!(list instanceof HTMLElement)) return false

  const items = [...list.querySelectorAll<HTMLElement>(`[${KEY_ITEM_ATTR}]`)]
  if (!items.length) return false

  const current = active instanceof HTMLElement ? items.indexOf(active.closest(`[${KEY_ITEM_ATTR}]`)!) : -1

  // Фокуса в списке ещё не было — заходим с края по направлению движения.
  const nextIndex =
    current < 0
      ? direction === 1
        ? 0
        : items.length - 1
      : Math.min(items.length - 1, Math.max(0, current + direction))

  const next = items[nextIndex]
  if (!next) return false

  next.focus()
  next.scrollIntoView({ block: 'nearest' })
  return true
}

export function startHotkeys(actions: HotkeyActions): () => void {
  const onKeyDown = (event: KeyboardEvent): void => {
    if (!hotkeysAvailable()) return
    if (event.altKey) return

    const meta = event.metaKey || event.ctrlKey

    // `Ctrl/Cmd + K` — единственное сочетание с модификатором, которое
    // перехватывается: оно не занято браузерами под собственное действие.
    if (meta && event.key.toLowerCase() === 'k') {
      event.preventDefault()
      actions.focusChatSearch()
      return
    }

    // Остальные сочетания — без модификаторов: перехватывать браузерные ЗАПРЕЩЕНО.
    if (meta) return

    // В поле ввода работают только клавиши самого поля (§4.6, контекст).
    if (isTextEntry(event.target)) return

    switch (event.key) {
      case '?':
        event.preventDefault()
        actions.openShortcuts()
        break

      case '/':
        event.preventDefault()
        actions.focusChatSearch()
        break

      case 'ArrowDown':
        if (moveWithinList(1)) event.preventDefault()
        break

      case 'ArrowUp':
        if (moveWithinList(-1)) event.preventDefault()
        break

      default:
        break
    }
  }

  globalThis.addEventListener('keydown', onKeyDown)
  return () => globalThis.removeEventListener('keydown', onKeyDown)
}
